import type { ReactNode } from 'react';
import { useStudentCommitments, useStudentCycles } from '../../api/hooks';
import { EmptyState, Badge, Chip, Spinner, ErrorPanel, StatStrip, Term, Track, SortableTh, PagedFooter, Table, Td } from '../../components/ui';
import { useSortableTable, type SortColumn } from '../../lib/useSortableTable';
import { usePagination } from '../../lib/usePagination';
import type { CommitmentsResponse } from '../../api/types';
import { FaClipboardList, FaCheck } from '../../components/ui/icons';
import Box from './components/Box';
import { humanize } from '../../lib/formatLabels';
import { commitmentPace } from './lib/derive';
import { dayMonth, parseDate } from './lib/format';
import { TH_RULE } from '../../lib/tableRules';

function MilestoneList({ fired }: { fired: number[] }) {
  return (
    <ul>
      {[25, 50, 75, 100].map((m) => {
        const hit = (fired ?? []).includes(m);
        return (
          <li key={m} className="flex items-center gap-2.5 py-1.5 text-[13px]">
            <span aria-hidden="true"
              className={`grid h-5 w-5 flex-none place-items-center rounded-full border-[1.5px] text-[10px] ${
                hit ? 'border-success bg-success text-white' : 'border-line-strong text-transparent'}`}>
              <FaCheck />
            </span>
            <span className={`flex-1 ${hit ? 'text-ink' : 'text-muted'}`}>{m}% of target</span>
            <span className="font-mono text-xs text-muted">{hit ? 'Reached' : 'Not yet'}</span>
          </li>
        );
      })}
    </ul>
  );
}

/** Whole days from today to an ISO deadline; null when past or unparsable. */
function daysLeft(deadline: string): number | null {
  const d = new Date(`${deadline}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const n = Math.ceil((d.getTime() - Date.now()) / 86_400_000);
  return n >= 0 ? n : null;
}

type HistoryRow = CommitmentsResponse['history'][number];

const HISTORY_COLUMNS: SortColumn<HistoryRow>[] = [
  { key: 'goal', accessor: (c) => c.kind },
  { key: 'period', accessor: (c) => c.started_at ?? c.deadline, type: 'date' },
  { key: 'result', accessor: (c) => c.last_progress_value, type: 'number' },
  { key: 'outcome', accessor: (c) => c.status },
];

const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Jun–Jul 2025" (artboard period): start to finish, collapsing a shared month or year. */
export function periodLabel(startIso: string | null | undefined, endIso: string | null | undefined): string {
  const a = parseDate(startIso);
  const b = parseDate(endIso);
  if (!a && !b) return '—';
  if (!a || !b) { const d = (a ?? b) as Date; return `${MONTH[d.getMonth()]} ${d.getFullYear()}`; }
  if (a.getFullYear() === b.getFullYear()) {
    return a.getMonth() === b.getMonth()
      ? `${MONTH[a.getMonth()]} ${a.getFullYear()}`
      : `${MONTH[a.getMonth()]}–${MONTH[b.getMonth()]} ${a.getFullYear()}`;
  }
  return `${MONTH[a.getMonth()]} ${a.getFullYear()}–${MONTH[b.getMonth()]} ${b.getFullYear()}`;
}

type PlanItem = CommitmentsResponse['plan_items'][number];
const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Plan items grouped by day, in order, each labelled "Mon 12". */
function planDays(items: PlanItem[]): { day: string; label: string; items: PlanItem[] }[] {
  const byDay = new Map<string, PlanItem[]>();
  for (const it of items) byDay.set(it.day, [...(byDay.get(it.day) ?? []), it]);
  return [...byDay.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([day, rows]) => {
    const d = parseDate(day);
    return { day, label: d ? `${WEEKDAY[d.getDay()]} ${d.getDate()}` : day, items: rows };
  });
}

/** A day's items by subject: how many, how many done or skipped, total minutes. */
function bySubject(items: PlanItem[]): { name: string; total: number; done: number; skipped: number; minutes: number }[] {
  const out = new Map<string, { name: string; total: number; done: number; skipped: number; minutes: number }>();
  for (const it of items) {
    const g = out.get(it.subject_name) ?? { name: it.subject_name, total: 0, done: 0, skipped: 0, minutes: 0 };
    g.total += 1;
    if (it.status === 'completed') g.done += 1;
    if (it.status === 'skipped') g.skipped += 1;
    g.minutes += it.estimated_minutes ?? 0;
    out.set(it.subject_name, g);
  }
  return [...out.values()];
}

const OUTCOME: Record<string, { label: string; tone: 'success' | 'danger' | 'neutral' }> = {
  completed: { label: 'Met', tone: 'success' },
  lapsed: { label: 'Not met', tone: 'danger' },
};

function PastCommitmentsTable({ history }: { history: HistoryRow[] }) {
  const { rows, sort, toggle } = useSortableTable(history, HISTORY_COLUMNS, { key: 'period', dir: 'desc' });
  const pg = usePagination(rows, 10, `${sort?.key}|${sort?.dir}`);
  return (
    <div>
    <Table minWidth={560}>
      <thead>
        <tr>
          <SortableTh columnKey="goal" sort={sort} onToggle={toggle} className={TH_RULE}>Goal</SortableTh>
          <SortableTh columnKey="period" sort={sort} onToggle={toggle} className={TH_RULE}>Period</SortableTh>
          <SortableTh columnKey="result" sort={sort} onToggle={toggle} align="right" className={TH_RULE}>Result</SortableTh>
          <SortableTh columnKey="outcome" sort={sort} onToggle={toggle} className={TH_RULE}>Outcome</SortableTh>
        </tr>
      </thead>
      <tbody>
        {pg.pageItems.map((c) => {
          const outcome = OUTCOME[c.status] ?? { label: humanize(c.status), tone: 'neutral' as const };
          return (
            <tr key={c.id}>
              <Td className="font-semibold capitalize">
                {c.kind.replace(/_/g, ' ')} <span className="font-normal text-muted">— target {c.target_value}</span>
              </Td>
              <Td className="whitespace-nowrap font-mono text-[13px] font-medium">{periodLabel(c.started_at ?? null, c.completed_at ?? c.deadline)}</Td>
              <Td align="right" className="font-mono font-medium">
                {c.last_progress_value != null ? `${c.last_progress_value} / ${c.target_value}` : '—'}
              </Td>
              <Td><Badge tone={outcome.tone}>{outcome.label}</Badge></Td>
            </tr>
          );
        })}
      </tbody>
    </Table>
    <PagedFooter className="mt-3.5" label="Past commitments pagination" {...pg} onPage={pg.setPage} />
    </div>
  );
}

/** Student 360 → Study plan: a summary strip, this week's plan beside the active
 *  commitment, the remediation cycles (``cycles``), then past commitments. */
export default function CommitmentsTab({ id, cycles }: { id: number; cycles?: ReactNode }) {
  const q = useStudentCommitments(id);
  const cyclesQ = useStudentCycles(id);
  if (q.isLoading) return <div className="flex justify-center py-8"><Spinner size="md" /></div>;
  if (q.isError || !q.data) return <ErrorPanel />;
  const { active, history, plan_items, current_week_start } = q.data;
  const doneCount = plan_items.filter((it) => it.status === 'completed').length;
  const current = active[0];
  const pct = current && current.target_value > 0 ? Math.min(100, Math.round((current.last_progress_value / current.target_value) * 100)) : null;
  const pace = current ? commitmentPace(current) : null;
  const met = history.filter((c) => c.status === 'completed').length;
  const runningCycles = (cyclesQ.data?.cycles ?? []).filter((c) => c.status !== 'closed').length;

  const planBox = (
        <Box title="This week's plan" help="week_plan"
          subtitle={current_week_start ? `Week of ${dayMonth(current_week_start)}` : undefined}
          actions={plan_items.length > 0 ? <Chip tone="neutral">{doneCount} of {plan_items.length} done</Chip> : undefined}>
          {plan_items.length === 0 ? (
            <EmptyState compact icon={<FaClipboardList />} title="No plan for this week" />
          ) : (
            // One row per day (at most seven), its subjects as chips: short however busy the week.
            <ul className="divide-y divide-hairline">
              {planDays(plan_items).map(({ day, label, items }) => (
                <li key={day} className="flex items-start gap-3 py-2 first:pt-0 last:pb-0">
                  <span className="w-[44px] flex-none pt-1 font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">{label}</span>
                  <span className="flex min-w-0 flex-1 flex-wrap gap-1.5">
                    {/* Read-only: the API has no teacher tick, so this mirrors the student's own status.
                        A subject planned several times that day is one chip with its count. */}
                    {bySubject(items).map((g) => {
                      const allDone = g.done === g.total;
                      const allSkipped = g.skipped === g.total;
                      const count = g.total > 1 ? `${g.done}/${g.total}` : null;
                      return (
                        <span key={g.name} title={`${g.name}: ${g.done} of ${g.total} done${g.minutes ? `, ${g.minutes} min` : ''}`}
                          className={`inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${
                            allDone ? 'border-success/40 bg-success-bg text-success' : allSkipped ? 'border-line text-muted line-through' : 'border-line-strong text-ink'}`}>
                          {allDone && <FaCheck aria-hidden />}
                          <span className="truncate">{g.name}</span>
                          {count && <span className="flex-none font-mono font-medium opacity-75">{count}</span>}
                          {g.minutes > 0 && <span className="flex-none font-mono font-medium opacity-60">{g.minutes}m</span>}
                          <span className="sr-only">{`, ${g.done} of ${g.total} done`}</span>
                        </span>
                      );
                    })}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {plan_items.length > 0 && (
            <Track value={(doneCount / plan_items.length) * 100} tone="slate" className="mt-3.5"
              label={`${doneCount} of ${plan_items.length} plan tasks done`} />
          )}
        </Box>
  );

  return (
    <div className="space-y-4">
      <StatStrip boxed columns={4} items={[
        { label: 'This week', value: plan_items.length ? `${doneCount}/${plan_items.length}` : '—', hint: plan_items.length ? 'plan tasks done' : 'no plan yet',
          tone: plan_items.length && doneCount === plan_items.length ? 'success' : undefined },
        { label: 'Commitment', value: pct == null ? '—' : `${pct}%`, hint: current ? (pace === 'behind' ? 'behind pace' : 'on track') : 'none active',
          tone: pace === 'behind' ? 'warning' : pct != null ? 'success' : undefined },
        { label: 'Remediation', value: cyclesQ.data ? runningCycles : '—', hint: runningCycles === 1 ? 'cycle running' : 'cycles running' },
        { label: 'Past goals met', value: history.length ? `${met}/${history.length}` : '—', hint: history.length ? 'commitments' : 'none yet' },
      ]} />

      <div className="grid items-start gap-4 2xl:grid-cols-2">
        {planBox}
        <Box title="Active commitment" help="commitment"
          actions={(() => { const pace = active[0] ? commitmentPace(active[0]) : null; return pace ? <Badge tone={pace === 'behind' ? 'warning' : 'success'} size="sm">{pace === 'behind' ? 'Behind' : 'On track'}</Badge> : undefined; })()}>
          {active.length === 0 ? (
            <EmptyState compact icon={<FaClipboardList />} title="No active commitment" />
          ) : (
            <div className="space-y-4">
              {active.map((c) => {
                const unit = (c as any).target_unit ? ` (${(c as any).target_unit})` : '';
                const left = daysLeft(c.deadline);
                const pct = c.target_value > 0 ? (c.last_progress_value / c.target_value) * 100 : 0;
                return (
                  <div key={c.id}>
                    <p className="font-display text-[19px] font-bold capitalize leading-tight text-ink">{c.kind.replace(/_/g, ' ')}</p>
                    <p className="mb-3.5 mt-1.5 text-[13px] text-muted">
                      Started {dayMonth(c.started_at)} · due {dayMonth(c.deadline)}{left !== null && ` · ${left} days left`} · target {c.target_value}{unit}
                    </p>
                    <div className="mb-1.5 flex justify-between text-[13px] text-muted">
                      <span>Now <span className="font-mono text-ink">{c.last_progress_value}</span></span>
                      <span>Goal <span className="font-mono text-ink">{c.target_value}</span></span>
                    </div>
                    <div className="relative mb-4">
                      <Track value={pct} tone="accent" label={`Progress towards ${c.kind.replace(/_/g, ' ')}`} />
                      {/* Goal marker at the end of the track, as on the artboard (the fill is progress towards the goal). */}
                      {c.target_value > 0 && (
                        <span aria-hidden="true" data-testid="goal-marker" title="Goal"
                          className="pointer-events-none absolute -top-1 h-4 w-[3px] -translate-x-px rounded-sm bg-navy dark:bg-mist-fixed"
                          style={{ left: '100%' }} />
                      )}
                    </div>
                    <p className="mb-1 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted"><Term k="milestones">Milestones</Term></p>
                    <MilestoneList fired={c.milestones_fired ?? []} />
                  </div>
                );
              })}
            </div>
          )}
        </Box>

      </div>

      {cycles}

      <Box title="Past commitments" help="past_commitments">
        {history.length === 0 ? (
          <EmptyState compact icon={<FaClipboardList />} title="No past commitments" />
        ) : (
          <PastCommitmentsTable history={history} />
        )}
      </Box>
    </div>
  );
}
